// page/auth/admin/modals/EmployeeEditModal.jsx (직책 + 권한 편집/유효권한/하단 고정 액션)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../../util/api";
import {
    Box, Collapse, Divider, IconButton, InputAdornment, Stack, TextField, Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { notify } from "../../../../util/toast";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";
import { runApiAction } from "../../../../util/apiFeedback";
import { API } from "../api/endpoints";
import FormField from "../../../../component/common/FormField";
import FormModal from "../../../../component/common/FormModal";
import ComboBox from "../../../../component/common/ComboBox";
import ActionButton from "../../../../component/common/ActionButton";
import LoadingDots from "../../../../component/common/LoadingDots";
import AccountIdField, { ACCOUNT_DOMAIN, toAccount } from "../../../../component/common/AccountIdField";
import PasswordField from "../../../../component/common/PasswordField";
import PermissionTree from "../../../../component/permission/PermissionTree";
import EffectivePermissionTree from "../../../../component/permission/EffectivePermissionTree";
import { personalFromTreeSelection, unionCodes } from "../../../../component/permission/permissionSets";
import { useMyPermissions } from "../../../../util/useMyPermissions";

const POSITION_REQUIRED = true;  // 직위(직급) 필수 여부
const DUTY_REQUIRED = false;     // 직책 필수 여부(선택 사항 — 유기훈·김준서처럼 직책 없는 계정 허용)
const ADMIN_CODE = "GLOBAL_ADMIN";

/** ID 중복 확인 상태별 안내 문구 색 */
const ID_CHECK_COLOR = {
    checking: "#94a3b8",
    ok:       "#16a34a",
    taken:    "#dc2626",
    error:    "#b45309",
};

function formatPhoneNumber(value) {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

/** 접었다 펼치는 섹션 헤더 + 본문 */
function ToggleSection({ icon, title, hint, open, onToggle, children }) {
    return (
        <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
            <Box
                onClick={onToggle}
                sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    px: 1.5, py: 1.1, cursor: "pointer", userSelect: "none",
                    bgcolor: open ? "#f8fafc" : "#fff",
                    "&:hover": { bgcolor: "#f8fafc" },
                }}
            >
                {icon}
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", flex: 1 }}>
                    {title}
                </Typography>
                {hint && <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{hint}</Typography>}
                <ExpandMoreRoundedIcon sx={{
                    fontSize: 20, color: "#94a3b8",
                    transform: open ? "rotate(180deg)" : "none",
                    transition: "transform .18s ease",
                }} />
            </Box>
            <Collapse in={open} unmountOnExit>
                <Divider />
                <Box sx={{ p: 1.5, bgcolor: "#fff" }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

export default function EmployeeEditModal({
                                              open,
                                              onClose,
                                              departments,
                                              positions,
                                              duties,
                                              initial,
                                              onSaved,
                                              onDeleted,
                                          }) {
    const isEdit = !!initial;
    const { isAdmin } = useMyPermissions();

    const deptOptions = useMemo(() => (Array.isArray(departments) ? departments : []), [departments]);
    const posOptions = useMemo(() => (Array.isArray(positions) ? positions : []), [positions]);
    const dutyOptions = useMemo(() => (Array.isArray(duties) ? duties : []), [duties]);

    /* ── 권한 편집 상태 ──────────────────────────────────────────────────────
     * 개인 권한(personalSet)이 유일한 편집 대상이고, 부서 권한(deptSet)은 <b>선택된 부서</b>에서
     * 파생되는 읽기 전용 값이다. 예전엔 둘을 합친 checked 하나만 들고 개인 권한을 (checked − 부서)로
     * 역산했는데, 그러면 부서와 겹치는 개인 권한이 저장할 때 조용히 회수됐다. 부서를 바꿀 수 있게
     * 되면서 이 문제가 훨씬 잘 드러나므로(부서 A → B 로 바꾸면 A 와 겹쳤던 개인 권한이 증발) 분리한다.
     * ---------------------------------------------------------------------- */
    const [allPerms, setAllPerms] = useState([]);           // [{code, nameKr, domain, tab, description}]
    const [deptSet, setDeptSet] = useState(new Set());      // 선택된 부서의 자동 권한(트리에서 잠금)
    const [personalSet, setPersonalSet] = useState(new Set()); // 개인 권한 — 저장 대상
    const [permLoaded, setPermLoaded] = useState(false);    // 개인 권한을 정상적으로 읽었는가(저장 가드)
    const [permLoading, setPermLoading] = useState(false);  // 권한 목록·개인 권한 로딩
    const [deptPermLoading, setDeptPermLoading] = useState(false); // 부서 권한 로딩(부서 바꿀 때마다)
    /**
     * 부서 권한 조회 실패 — "0개"와 반드시 구분한다.
     *
     * <p>{@code GET /api/permissions/department/{id}} 는 DEPT_READ·HR_DEPT_READ 를 요구한다.
     * 직원은 고칠 수 있지만(EMPLOYEE_UPDATE) 부서 권한은 못 읽는 담당자가 열면 403 이 떨어지는데,
     * 이때 "이 부서 권한 0개"라고 써버리면 실제로는 권한이 붙는 부서를 비어 있는 것처럼 보여주게 된다.
     */
    const [deptPermError, setDeptPermError] = useState(false);
    const [permOpen, setPermOpen] = useState(false);        // 권한 편집 토글 — 기본 닫힘
    const [effOpen, setEffOpen] = useState(true);           // 유효 권한 토글 — 부서 효과가 바로 보이도록 기본 열림

    /** 부서 권한 캐시 — 부서를 이리저리 바꿔볼 때 같은 부서를 반복 조회하지 않는다(모달 열 때 비운다). */
    const deptPermCache = useRef(new Map());

    /**
     * 폼이 이번에 연 대상으로 채워졌는가.
     *
     * <p>모달을 닫아도 form 은 직전 값을 들고 있어서, 다시 열린 첫 렌더의 form.deptId 는 <b>이전 직원</b>의
     * 부서다. 이때 부서 권한을 읽으면 남의 부서 권한 개수가 한 번 스쳐 지나간다 — 폼이 채워진 뒤부터 읽는다.
     */
    const [formReady, setFormReady] = useState(false);
    useEffect(() => { if (!open) setFormReady(false); }, [open]);

    const [form, setForm] = useState({
        employeeCode: "",
        name: "",
        email: "",
        phoneNumber: "",
        deptId: "",
        positionId: "",
        dutyId: "",
        gender: "NONE",
        birthDate: "",
        joinDate: "",
        retirePlannedDate: "",
    });

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    /**
     * 저장된 ID 에 '@' 가 섞인 레거시 계정 — 값을 <b>건드리지 않는다</b>.
     *
     * <p>ID 입력칸은 로컬파트만 다루므로(도메인은 뒤에 표기) 여기에 'ksw@onandme.com' 을 물리면
     * 도메인을 벗겨 보여주게 되고, 다른 항목만 고쳐 저장해도 저장값이 조용히 'ksw' 로 바뀐다.
     * 그래서 이런 행은 일반 입력칸으로 <b>그대로</b> 보여주고, 정규화도 중복 검사도 하지 않는다.
     */
    const isLegacyEmail = isEdit && String(initial?.email || "").includes("@");

    // 관리자 강제 비밀번호 설정용 서브 모달 상태 (초기화와 별개 — 특정 값으로 직접 지정)
    const [pwDlg, setPwDlg] = useState({ open: false, value: "", saving: false, show: false });

    /** ID 중복 확인 상태 — idle | checking | ok | taken | error */
    const [idCheck, setIdCheck] = useState({ state: "idle", msg: "" });

    /**
     * 편집 불가 코드와 사유 — PermissionTree 가 잠금 표시·툴팁에 쓴다.
     *  1) 부서 자동 권한: 개인 권한 화면에서 뗄 수 없다(부서 권한 화면에서 관리).
     *  2) GLOBAL_ADMIN: 전체 관리자만 켜고 끌 수 있다(권한 승격 차단).
     *     서버(PermissionGrantGuard)가 최종 방어선이고, 여기 잠금은 403 을 미리 안 맞게 하는 용도.
     */
    const locks = useMemo(() => {
        const m = new Map();
        for (const c of deptSet) m.set(c, "부서 권한으로 자동 부여됨 — 부서 권한 설정에서 변경하세요.");
        if (!isAdmin) m.set(ADMIN_CODE, "전체 관리자 권한은 전체 관리자만 부여·회수할 수 있습니다.");
        return m;
    }, [deptSet, isAdmin]);

    // 유효 권한 = 부서 ∪ 개인. 부서가 바뀌면 deptSet 이 갈리므로 이 값들도 바로 따라간다.
    const deptCodes = useMemo(() => [...deptSet], [deptSet]);
    const personalCodes = useMemo(() => [...personalSet], [personalSet]);
    // 트리에 보여줄 체크 상태 — 부서 권한은 잠긴 채로 켜져 있어야 "이미 갖고 있음"이 보인다.
    const checked = useMemo(() => unionCodes(deptSet, personalSet), [deptSet, personalSet]);
    const effectiveCount = checked.size;

    /** 트리 체크 변경 → 개인 권한에만 반영(규칙과 이유는 permissionSets.js). */
    const applyTreeChange = (next) =>
        setPersonalSet((prev) => personalFromTreeSelection(next, deptSet, prev));

    /* ── 권한 목록 + 개인 권한 로드 (모달을 열 때 한 번) ──────────────────────
     * 부서 권한은 여기서 안 읽는다 — 선택된 부서를 따라가야 하므로 아래 별도 effect 가 맡는다.
     * 생성 모드에서도 목록은 읽는다(개인 권한을 미리 골라서 함께 생성할 수 있어야 하므로).
     * ---------------------------------------------------------------------- */
    useEffect(() => {
        if (!open) return;
        let alive = true;

        deptPermCache.current = new Map(); // 열 때마다 비운다 — 그사이 부서 권한이 바뀌었을 수 있다
        setPermLoading(true);
        setPermLoaded(false);
        // 부서 권한은 아직 모른다 — 0개라고 단정하지 말고 '확인 중'으로 보여준다(아래 부서 effect 가 끈다).
        // 직전에 열었던 직원의 부서 권한이 남아 있으므로 반드시 비운다 — 부서 조회가 개인 권한 조회보다
        // 늦게 끝나면 그 사이 남의 부서 권한이 유효 권한 트리에 그대로 보인다.
        setDeptSet(new Set());
        setDeptPermLoading(true);

        (async () => {
            try {
                const [all, personal] = await Promise.all([
                    api.get(API.adminPermissions.listAll)
                        .then((d) => (Array.isArray(d) ? d : (d?.data || []))),
                    initial?.employeeCode
                        ? api.get(API.adminPermissions.employeeGet(initial.employeeCode))
                            .then((d) => (Array.isArray(d) ? d : (d?.data || [])).map(String))
                        : Promise.resolve([]),
                ]);
                if (!alive) return;
                setAllPerms(all);
                setPersonalSet(new Set(personal));
                setPermLoaded(true);
            } catch {
                if (!alive) return;
                setAllPerms([]);
                setPersonalSet(new Set());
                setPermLoaded(false);
            } finally {
                if (alive) setPermLoading(false);
            }
        })();

        return () => { alive = false; };
    }, [open, initial]);

    /* ── 선택된 부서의 자동 권한 (부서를 바꾸면 즉시 다시 읽는다) ─────────────
     * 부서를 고른 순간 "이 부서면 뭐가 붙는지"가 보여야 한다. 저장하고 목록으로 돌아와서야
     * 알게 되면 부서를 잘못 고른 걸 되돌리는 데 왕복이 한 번 더 든다.
     * ---------------------------------------------------------------------- */
    useEffect(() => {
        if (!open || !formReady) return;
        const deptId = form.deptId;
        if (deptId === "" || deptId == null) {
            setDeptSet(new Set());
            setDeptPermError(false);
            setDeptPermLoading(false);
            return;
        }

        const key = String(deptId);
        const cached = deptPermCache.current.get(key);
        if (cached) {
            setDeptSet(new Set(cached));
            setDeptPermError(false);
            setDeptPermLoading(false);
            return;
        }

        let alive = true;
        setDeptPermLoading(true);
        api.get(API.adminPermissions.deptGet(deptId))
            .then((d) => {
                if (!alive) return;
                const codes = (Array.isArray(d) ? d : (d?.data || [])).map(String);
                // 실패는 캐시하지 않는다 — 부서를 다시 고르면 재시도할 수 있게
                deptPermCache.current.set(key, codes);
                setDeptSet(new Set(codes));
                setDeptPermError(false);
            })
            .catch(() => { if (alive) { setDeptSet(new Set()); setDeptPermError(true); } })
            .finally(() => { if (alive) setDeptPermLoading(false); });

        return () => { alive = false; };
    }, [open, formReady, form.deptId]);

    useEffect(() => {
        if (!open) return;
        setPermOpen(false);
        setEffOpen(true);

        if (initial) {
            setForm({
                employeeCode: initial.employeeCode || "",
                name: initial.name || "",
                email: initial.email || "",
                phoneNumber: initial.phoneNumber || "",
                deptId: initial.deptId ?? "",
                positionId: initial.positionId ?? "",
                dutyId: initial.dutyId ?? "",
                gender: initial.gender || "NONE",
                birthDate: initial.birthDate || "",
                joinDate: initial.joinDate || "",
                retirePlannedDate: initial.retirePlannedDate || "",
            });
        } else {
            setForm({
                employeeCode: "",
                name: "",
                email: "",
                phoneNumber: "",
                deptId: deptOptions?.[0]?.deptId ?? "",
                positionId: "",
                dutyId: "",
                gender: "NONE",
                birthDate: "",
                joinDate: "",
                retirePlannedDate: "",
            });
        }
        setIdCheck({ state: "idle", msg: "" });
        setFormReady(true);   // 이제부터 form.deptId 는 이번에 연 대상의 부서다 → 부서 권한을 읽어도 된다
    }, [open, initial, deptOptions]); // eslint-disable-line

    /* ── ID 중복 확인 ────────────────────────────────────────────────────────
     * 서버(EmployeeService.createEmployee/update)가 최종 방어선이지만, 폼을 다 채우고 '생성' 을 눌러서야
     * "이미 사용 중" 을 만나면 그 자리에서 다시 입력해야 한다. 그래서 타이핑이 멎으면 바로 확인한다.
     * 대소문자는 같은 ID 로 본다 — 메일 수신 매칭이 IgnoreCase 라 'KSW' 와 'ksw' 는 사실상 같은 사서함.
     * ---------------------------------------------------------------------- */
    useEffect(() => {
        if (!open || isLegacyEmail) return;                 // 레거시 계정은 값을 안 건드리므로 검사도 안 한다
        const id = toAccount(form.email);
        // 빈 값은 '필수' 검증이 잡는다. 수정 화면에서 ID 를 그대로 뒀으면 확인할 것도 없다.
        if (!id || (isEdit && id === toAccount(initial?.email))) {
            setIdCheck({ state: "idle", msg: "" });
            return;
        }

        let alive = true;
        setIdCheck({ state: "checking", msg: "중복 확인 중…" });
        const timer = setTimeout(async () => {
            try {
                const d = await api.get(API.employees.checkLoginId, {
                    email: id,
                    excludeEmployeeCode: initial?.employeeCode,   // 수정 화면에서 자기 자신은 제외
                });
                if (!alive) return;
                setIdCheck(d?.available
                    ? { state: "ok",    msg: `사용 가능한 ID 입니다 — ${id}${ACCOUNT_DOMAIN}` }
                    : { state: "taken", msg: `이미 사용 중인 ID 입니다 — ${id}` });
            } catch {
                // 확인이 실패해도 저장을 막지는 않는다 — 서버가 저장 시 다시 검사한다.
                if (alive) setIdCheck({ state: "error", msg: "중복 확인 실패 — 저장할 때 다시 검사합니다." });
            }
        }, 400);
        return () => { alive = false; clearTimeout(timer); };
    }, [open, form.email, isEdit, isLegacyEmail, initial]);

    const validate = () => {
        if (!form.name?.trim()) return "이름은 필수입니다.";
        if (!form.email?.trim()) return "ID는 필수입니다.";
        // 확인 중/실패 상태에서 저장을 막지는 않는다 — 서버가 같은 검사를 다시 하고 메시지도 그대로 뜬다.
        if (idCheck.state === "taken") return idCheck.msg;
        if (!form.phoneNumber?.trim()) return "연락처는 필수입니다.";
        if (form.phoneNumber.replace(/\D/g, "").length < 10) return "연락처를 올바르게 입력하세요.";
        if (!form.deptId) return "부서를 선택하세요.";
        if (!form.gender) return "성별을 선택하세요.";
        if (!form.joinDate) return "입사일(joinDate)은 필수입니다.";

        if (POSITION_REQUIRED && !form.positionId) return "직위(직급)은 필수입니다.";
        if (DUTY_REQUIRED && !form.dutyId) return "직책은 필수입니다.";

        return null;
    };

    const toNullableDate = (v) => (v && String(v).trim() ? v : null);
    const toNullableNumber = (v) => (v === "" || v == null ? null : Number(v));

    const save = () => {
        const msg = validate();
        if (msg) return notify(msg, "warning");

        const payload = {
            name: form.name.trim(),
            // employee.email 에는 로컬파트만 들어가야 한다 — 로그인이 이 값으로 정확히 조회하므로
            // 도메인이 섞이면 그 계정은 아예 로그인이 안 된다(AuthController.login → findByEmail).
            // 단, 레거시 계정은 저장값을 그대로 보낸다(정규화하지 않는다 — isLegacyEmail 주석 참고).
            email: isLegacyEmail ? form.email.trim() : toAccount(form.email),
            phoneNumber: form.phoneNumber.trim(),
            deptId: Number(form.deptId),
            positionId: toNullableNumber(form.positionId),
            dutyId: toNullableNumber(form.dutyId),
            gender: form.gender,
            birthDate: toNullableDate(form.birthDate),
            joinDate: form.joinDate,
        };

        if (isEdit) {
            const beforeDeptId = initial?.deptId ?? null;
            const nextPersonal = personalCodes; // 부서 자동 권한은 여기 없다 — 부서 권한 설정에서 관리
            return runApiAction({
                request: async () => {
                    await api.put(API.employees.update(initial.employeeCode), {
                        ...payload,
                        retirePlannedDate: toNullableDate(form.retirePlannedDate),
                    });
                    // 권한이 정상 로드된 경우에만 개인 권한 반영(로드 실패 시 덮어쓰기 방지)
                    if (permLoaded) {
                        await api.put(API.adminPermissions.employeeReplace(initial.employeeCode), {
                            codes: nextPersonal,
                        });
                    }
                },
                successMsg: "직원 정보가 저장되었습니다.",
                onSuccess: () => onSaved?.({ mode: "update", beforeDeptId, afterDeptId: Number(form.deptId) }),
            });
        } else {
            // 생성과 동시에 개인 권한을 부여한다 — 부서 권한은 서버가 부서에서 알아서 붙이므로 보내지 않는다.
            return runApiAction({
                request: () => api.post(API.employees.create, {
                    ...payload,
                    employeePermissionCodes: personalCodes,
                }),
                successMsg: personalCodes.length
                    ? `직원이 생성되었습니다. (개인 권한 ${personalCodes.length}개 부여)`
                    : "직원이 생성되었습니다.",
                onSuccess: () => onSaved?.({ mode: "create" }),
            });
        }
    };

    const handleDelete = async () => {
        if (!isEdit) return;
        if (!(await confirmDialog({ message: `직원(${form.name || form.employeeCode})을 삭제할까요?` }))) return;
        return runApiAction({
            request: () => api.delete(API.employees.delete(initial.employeeCode)),
            successMsg: "직원이 삭제되었습니다.",
            onSuccess: () => (onDeleted ?? onClose)?.(),
        });
    };

    const handleResetPassword = async () => {
        if (!isEdit) return;
        if (!(await confirmDialog({ message: `${form.name || form.employeeCode} 비밀번호를 초기화할까요?` }))) return;
        return runApiAction({
            request: () => api.post(API.employees.resetPassword(initial.employeeCode)),
            successMsg: "비밀번호가 초기화되었습니다.",
            errorMsg: "초기화 실패",
        });
    };

    // 관리자가 비밀번호를 특정 값으로 강제 설정 — 복잡도/길이 제한 없음(빈 값만 방어).
    const openSetPassword = () => {
        if (!isEdit) return;
        setPwDlg({ open: true, value: "", saving: false, show: false });
    };

    const submitSetPassword = () => {
        if (!pwDlg.value) return notify("설정할 비밀번호를 입력하세요.", "warning");
        return runApiAction({
            request: () => api.post(API.employees.setPassword(initial.employeeCode), { password: pwDlg.value }),
            successMsg: "비밀번호가 설정되었습니다.",
            errorMsg: "비밀번호 설정 실패",
            setLoading: (v) => setPwDlg((p) => ({ ...p, saving: v })),
            onSuccess: () => setPwDlg({ open: false, value: "", saving: false, show: false }),
        });
    };

    return (
        <>
        <FormModal
            open={open}
            onClose={onClose}
            title={isEdit ? "직원 수정" : "직원 생성"}
            subtitle={isEdit ? `사번: ${form.employeeCode}` : "부서 지정 시 부서별 권한이 자동 할당됩니다"}
            maxWidth="md"
            onSave={save}
            saveLabel={isEdit ? "수정" : "생성"}
            footerExtras={isEdit && (
                <Stack direction="row" spacing={1}>
                    <ActionButton variant="delete" onClick={handleDelete} sx={{ fontSize: 12 }} />
                    <ActionButton variant="secondary" startIcon={false}
                        onClick={handleResetPassword}
                        sx={{ fontSize: 12 }}
                        label="비번 초기화" />
                    <ActionButton variant="secondary" startIcon={false}
                        onClick={openSetPassword}
                        sx={{ fontSize: 12 }}
                        label="비밀번호 강제 설정" />
                </Stack>
            )}
        >
            <Stack spacing={2}>
                {isEdit && (
                    <FormField label="사번">
                        <TextField value={form.employeeCode} disabled size="small" fullWidth />
                    </FormField>
                )}

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <FormField label="이름" required sx={{ flex: 1 }}>
                        <TextField fullWidth size="small" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="이름을 입력하세요" />
                    </FormField>
                    {/* 로그인 ID — 로그인 화면과 같은 입력칸을 쓴다(도메인은 뒤에 자동 표기, 값은 로컬파트만). */}
                    <FormField label="ID" required sx={{ flex: 1 }}>
                        {isLegacyEmail ? (
                            <TextField
                                fullWidth
                                size="small"
                                value={form.email}
                                onChange={(e) => set("email", e.target.value)}
                                helperText="도메인이 포함된 예전 계정 — 저장된 값을 그대로 유지합니다."
                            />
                        ) : (
                            <>
                                <AccountIdField value={form.email} onChange={(v) => set("email", v)} />
                                {idCheck.msg && (
                                    <Typography sx={{ fontSize: 12, mt: 0.5, color: ID_CHECK_COLOR[idCheck.state] }}>
                                        {idCheck.msg}
                                    </Typography>
                                )}
                            </>
                        )}
                    </FormField>
                </Stack>

                <FormField label="연락처" required>
                    <TextField
                        fullWidth
                        size="small"
                        value={form.phoneNumber}
                        onChange={(e) => set("phoneNumber", formatPhoneNumber(e.target.value))}
                        placeholder="010-1234-5678"
                        inputProps={{ inputMode: "numeric", maxLength: 13 }}
                    />
                </FormField>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <FormField label="부서" required sx={{ flex: 1 }}>
                        <ComboBox
                            clearable={false}
                            placeholder="부서"
                            options={deptOptions.map((d) => ({ value: d.deptId, label: d.deptName }))}
                            value={form.deptId}
                            onChange={(v) => set("deptId", v)}
                        />
                        {/* 부서를 고르는 그 자리에서 몇 개가 자동으로 붙는지 알려준다 — 자세한 목록은 아래 유효 권한. */}
                        <Typography sx={{
                            fontSize: 12, mt: 0.5,
                            color: deptPermLoading ? "#94a3b8" : (deptPermError ? "#b45309" : "#1d4ed8"),
                        }}>
                            {deptPermLoading
                                ? "부서 권한 확인 중…"
                                : deptPermError
                                    ? "부서 권한을 확인할 수 없습니다 — 부서 권한 조회 권한이 없거나 조회에 실패했습니다."
                                    : `이 부서 권한 ${deptCodes.length}개가 자동 부여됩니다`}
                        </Typography>
                    </FormField>

                    <FormField label="직위(직급)" required={POSITION_REQUIRED} sx={{ flex: 1 }}>
                        <ComboBox
                            clearable={false}
                            placeholder="직위(직급)"
                            options={[
                                ...(!POSITION_REQUIRED ? [{ value: "", label: "미선택" }] : []),
                                ...posOptions.map((p) => ({ value: p.positionId, label: p.positionName })),
                            ]}
                            value={form.positionId}
                            onChange={(v) => set("positionId", v)}
                        />
                    </FormField>

                    <FormField label="직책" required={DUTY_REQUIRED} sx={{ flex: 1 }}>
                        <ComboBox
                            clearable={!DUTY_REQUIRED}
                            placeholder="직책"
                            options={[
                                ...(!DUTY_REQUIRED ? [{ value: "", label: "미선택" }] : []),
                                ...dutyOptions.map((d) => ({ value: d.dutyId, label: d.dutyName })),
                            ]}
                            value={form.dutyId}
                            onChange={(v) => set("dutyId", v)}
                        />
                    </FormField>
                </Stack>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <FormField label="성별" required sx={{ flex: 1 }}>
                        <ComboBox
                            clearable={false}
                            placeholder="성별"
                            options={[
                                { value: "MALE", label: "남성(MALE)" },
                                { value: "FEMALE", label: "여성(FEMALE)" },
                                { value: "NONE", label: "미지정(NONE)" },
                            ]}
                            value={form.gender}
                            onChange={(v) => set("gender", v)}
                        />
                    </FormField>

                    <FormField label="생년월일" sx={{ flex: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            value={form.birthDate}
                            onChange={(e) => set("birthDate", e.target.value)}
                        />
                    </FormField>
                </Stack>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <FormField label="입사일" required sx={{ flex: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            value={form.joinDate}
                            onChange={(e) => set("joinDate", e.target.value)}
                        />
                    </FormField>
                    {isEdit && (
                        <FormField label="퇴사예정일" sx={{ flex: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                value={form.retirePlannedDate}
                                onChange={(e) => set("retirePlannedDate", e.target.value)}
                            />
                        </FormField>
                    )}
                </Stack>

                {/* ── 권한 — 생성·수정 모두에서 다룬다(생성 시에도 개인 권한을 함께 부여) ── */}
                <ToggleSection
                    title={isEdit ? "개인 권한 수정" : "개인 권한 추가"}
                    hint={permLoaded ? `개인 ${personalCodes.length} · 부서 ${deptCodes.length}` : undefined}
                    open={permOpen}
                    onToggle={() => setPermOpen((v) => !v)}
                >
                    {permLoading ? (
                        <Box sx={{ py: 3, textAlign: "center" }}><LoadingDots /></Box>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 1, lineHeight: 1.5 }}>
                                탭 체크로 그 탭의 권한을 한꺼번에 주고, 화살표를 열면 조회·관리를 따로 고를 수 있습니다.
                                {!isEdit && " 여기서 고른 권한은 직원 생성과 함께 부여됩니다."}
                                {!isAdmin && " 전체 관리자 권한은 전체 관리자만 변경할 수 있습니다."}
                            </Typography>
                            <PermissionTree
                                perms={allPerms}
                                selected={checked}
                                onChange={applyTreeChange}
                                locks={locks}
                            />
                        </>
                    )}
                </ToggleSection>

                {/* 유효 권한(부서 ∪ 개인) — 읽기 전용. 부서를 바꾸면 즉시 다시 그려진다. */}
                <ToggleSection
                    title="유효 권한"
                    hint={permLoaded ? `${effectiveCount}개` : undefined}
                    open={effOpen}
                    onToggle={() => setEffOpen((v) => !v)}
                >
                    {permLoading ? (
                        <Box sx={{ py: 3, textAlign: "center" }}><LoadingDots /></Box>
                    ) : (
                        <>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Typography sx={{ fontSize: 12, color: "#94a3b8", flex: 1, lineHeight: 1.5 }}>
                                    부서 권한 {deptCodes.length}개 + 개인 권한 {personalCodes.length}개
                                    {" "}= 실제로 쓸 수 있는 권한 {effectiveCount}개.
                                    부서를 바꾸면 바로 반영됩니다.
                                </Typography>
                                {deptPermLoading && <LoadingDots />}
                            </Stack>
                            <Divider sx={{ mb: 0.5 }} />
                            <EffectivePermissionTree
                                perms={allPerms}
                                deptCodes={deptSet}
                                personalCodes={personalSet}
                                emptyText="아직 부여된 권한이 없습니다 — 부서를 고르거나 위에서 개인 권한을 추가하세요."
                            />
                        </>
                    )}
                </ToggleSection>
            </Stack>
        </FormModal>

        {/* ── 비밀번호 강제 설정 서브 모달 (제한 없이 원하는 값으로 직접 설정) ── */}
        <FormModal
            open={pwDlg.open}
            onClose={() => !pwDlg.saving && setPwDlg((p) => ({ ...p, open: false }))}
            title="비밀번호 강제 설정"
            subtitle={`${form.name || form.employeeCode} · 원하는 비밀번호로 직접 설정합니다`}
            maxWidth="xs"
            onSave={submitSetPassword}
            saving={pwDlg.saving}
            saveLabel="설정"
        >
            <FormField label="새 비밀번호" required>
                <PasswordField
                    autoFocus
                    fullWidth
                    size="small"
                    type={pwDlg.show ? "text" : "password"}
                    value={pwDlg.value}
                    onChange={(v) => setPwDlg((p) => ({ ...p, value: v }))}
                    // 조합 중 Enter 는 '한글 확정' 이지 '설정' 이 아니다 — 조합이 끝난 Enter 만 받는다.
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing && !pwDlg.saving) submitSetPassword(); }}
                    placeholder="설정할 비밀번호 입력"
                    autoComplete="new-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={() => setPwDlg((p) => ({ ...p, show: !p.show }))}
                                    aria-label="비밀번호 표시 전환"
                                >
                                    {pwDlg.show
                                        ? <VisibilityOutlinedIcon fontSize="small" />
                                        : <VisibilityOffOutlinedIcon fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </FormField>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 1, lineHeight: 1.5 }}>
                길이·문자 제한이 없습니다. 설정한 비밀번호는 안전한 채널로 직원에게 직접 전달해 주세요.
            </Typography>
        </FormModal>
        </>
    );
}
